// Shoppable clips: the published output of the ML video demo (what clients see) and the
// curation API behind it (what admins use). Both are served by the Silvr CRUD service:
//   GET  /clips/<slug>                  public, the published clip with media links
//   *    /clips/curation/<path>         admin, proxied to the ML demo box (X-Curation-Passcode)
// In local development VITE_APP_CLIP_API_URL / VITE_APP_CURATION_URL can point straight at the
// box (http://<box>/api/public and http://<box>/api/curation), which answers the same shapes.

const API_BASE_URL = import.meta.env.VITE_APP_API_URL || 'https://silvr-crud-service.silvr.shop';
const API_KEY = import.meta.env.VITE_APP_API_KEY;
const CLIP_API_URL = (import.meta.env.VITE_APP_CLIP_API_URL || `${API_BASE_URL}/clips`).replace(/\/$/, '');
const CURATION_URL = (import.meta.env.VITE_APP_CURATION_URL || `${API_BASE_URL}/clips/curation`).replace(/\/$/, '');
// The viewer pass: the demo's passcode is checked by the API, which answers with an HttpOnly cookie on
// .silvr.shop, and the clip, its frames and its product photos are served only to a browser holding it.
// Empty when the deployment has no such endpoint (the ML box's interim host), and the gate falls back to
// the build-time passcode there.
const VIEWER_PASS_URL = (import.meta.env.VITE_APP_VIEWER_PASS_URL || `${API_BASE_URL}/viewer-pass`).replace(/\/$/, '');

// The board's view of who signed in and who may (SIL-1595); only the CRUD service has these routes.
const VIEWERS_URL = `${API_BASE_URL}/viewers`;
/** Where the Curate page and the board's Viewers panel keep the curation passcode for the tab. */
export const CURATION_PASSCODE_KEY = 'silvr-curation-passcode';

/** How a site signs viewers in (SIL-1595): a work email checked against the site's allowed domains, or the
 *  shared passcode. Decided by the API per site (viewer_sites); 'passcode' for a site it has no row for. */
export type SignInMode = 'email' | 'passcode';

export interface ViewerPassState {
  required: boolean;
  ok: boolean;
  /** How the site this browser is on signs in. Absent on an API older than SIL-1595. */
  mode?: SignInMode;
  /** The site the API took the request to be from (its Origin's host). */
  site?: string;
  /** The address this browser signed in with, when it did so by email and the pass is still good. */
  email?: string;
  /** The site also asks for a code emailed to the address (SIL-1618: the dev board). */
  code?: boolean;
  /** Set on the answer to an email on such a site: a code went out, ask for it next. */
  code_sent?: boolean;
}

/** An email, then on a site that asks for one the code emailed to it, or the passcode. */
export type ViewerCredential = { email: string; code?: string } | { passcode: string };

/** Does this deployment ask for a pass, how does this site sign in, and does this browser already hold one. */
export async function getViewerPass(): Promise<ViewerPassState> {
  const response = await fetch(VIEWER_PASS_URL, { credentials: 'include' });
  if (!response.ok) throw new Error(`Viewer pass unavailable (${response.status})`);
  return response.json();
}

/** Exchange a work email (sites in email mode) or the passcode for the pass cookie. On a site that asks for
 *  a code, the email alone answers `code_sent` (202, no cookie yet) and the email with the code gets the
 *  pass. Throws with a message the gate can show as is: the API's own plain `detail` (a refused or blocked
 *  address, a wrong code, a site that wants the other credential, too many attempts), or 'Incorrect passcode'. */
export async function createViewerPass(credential: ViewerCredential): Promise<ViewerPassState> {
  const response = await fetch(VIEWER_PASS_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credential),
  });
  if (response.ok) return response.json();
  if (response.status === 401) throw new Error('Incorrect passcode');
  let detail = '';
  try {
    detail = (await response.json()).detail ?? '';
  } catch {
    /* not JSON */
  }
  throw new Error(typeof detail === 'string' && detail ? detail : `Could not sign in (${response.status})`);
}

/** Hand the pass back (the cookie is HttpOnly, so only the API can drop it). Best effort: a failure only
 *  means the gate may let the browser straight through once more. */
export async function deleteViewerPass(): Promise<void> {
  try {
    await fetch(VIEWER_PASS_URL, { method: 'DELETE', credentials: 'include' });
  } catch {
    /* offline, or no endpoint */
  }
}

/** The clip was refused for want of a viewer pass (403): no pass, an expired one, or an address blocked
 *  since it signed in. The show page hands the pass back and sends the viewer to the gate on it. */
export class ClipAccessError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClipAccessError';
  }
}

// --- published (client) ---------------------------------------------------------------------

export interface ClipSource {
  file: string;
  fps: number;
  n_frames: number;
  width: number;
  height: number;
  duration: number;
}

/** One place a shopper can buy the product: a marketplace listing, a retailer page. */
export interface ProductLink {
  /** Who sells it there: "eBay", "Poshmark", "Nordstrom"... */
  marketplace: string;
  url: string;
  /** Listing price as the seller shows it, e.g. "$85". */
  price?: string;
  size?: string;
  /** Anything a shopper should know before clicking: condition, colourway. */
  note?: string;
}

export interface PublishedProduct {
  gallery_row: number;
  image: string | null;
  image_url: string | null;
  brand: string | null;
  name: string | null;
  category: string | null;
  merchant: string | null;
  listings: [string, string][] | null;
  score: number | null;
  match_type: string;
  // The fields below are not written by the pipeline (yet): a show's own product list
  // (src/shows/<key>/products.ts) fills them in when a person has confirmed the garment.
  /** Where "Shop at store" goes; a web search for brand + name when absent. */
  url?: string;
  /** Every place the product is on sale right now; the first one is the default. */
  links?: ProductLink[];
  /** Display price or range, e.g. "$65–$85"; "Check retailer" when absent. */
  price?: string;
  /** Colourway / variant as the brand names it, e.g. "Hillside", "Grass Green". */
  color?: string;
}

export interface PublishedTrack {
  /** [first, last] frame ranges the garment is on screen; more than one when it returns after a cut-away. */
  segments?: [number, number][];
  /** Shots the garment appears in (one per segment when bridged). */
  shots?: number[];
  bridged_from?: number[];
  id: number;
  shot: number;
  color?: string;
  start_frame: number;
  end_frame: number;
  start_t: number;
  end_t: number;
  /** [frame, x, y] with x/y normalised to the frame, one entry per frame of the track */
  path: [number, number, number][];
  product: PublishedProduct;
  alternates: PublishedProduct[];
}

export interface PublishedClip {
  slug: string;
  video: string;
  video_url: string;
  source: ClipSource;
  published_at: string;
  /** generated_at of the pipeline run the publish was made from (absent on older publishes). */
  run_generated_at?: string;
  tracks: PublishedTrack[];
}

export async function getPublishedClip(slug: string): Promise<PublishedClip> {
  // credentials: the viewer pass is a cookie on .silvr.shop; <video> and <img> send it by themselves.
  const response = await fetch(`${CLIP_API_URL}/${encodeURIComponent(slug)}`, { credentials: 'include' });
  if (response.status === 403) {
    throw new ClipAccessError(`This demo is behind a passcode (${response.status})`);
  }
  if (!response.ok) {
    throw new Error(`Clip ${slug} is not available (${response.status})`);
  }
  return response.json();
}

// --- curation (admin) -----------------------------------------------------------------------

export interface Candidate extends Omit<PublishedProduct, 'match_type' | 'image_url'> {
  rank: number;
  hits: number;
  best_rank: number;
  model: string;
  image_url?: string;
}

export interface TrackQuery {
  frame: number;
  t: number;
  score: number;
  sharpness?: number;
  box_px: [number, number, number, number];
  crop: string;
  thumb: string;
  thumb_url?: string;
  crop_url?: string;
  match_ms?: number;
}

export interface CurationTrack {
  /** [first, last] frame ranges the garment is on screen; more than one when it returns after a cut-away. */
  segments?: [number, number][];
  /** Shots the garment appears in (one per segment when bridged). */
  shots?: number[];
  bridged_from?: number[];
  id: number;
  shot: number;
  /** One hue per garment: the mask overlay, the hotspot and the timeline bar share it. */
  color?: string;
  /** Why the pipeline hides this garment by default (empty = shown): confidence, mask_area, box_side,
   *  box_area, sharpness. An admin can reveal it and choose a product for it anyway. */
  filtered?: string[];
  start_frame: number;
  end_frame: number;
  start_t: number;
  end_t: number;
  n_det: number;
  max_score: number;
  mean_score: number;
  mean_area_frac: number;
  /** Variance of the Laplacian of the sharpest query crop (focus measure). */
  sharpness?: number;
  path: [number, number, number][];
  boxes: [number, number, number, number, number][];
  queries: TrackQuery[];
  candidates: Candidate[];
}

export interface ClipFilters {
  conf: number;
  min_area_frac: number;
  engulf: number;
  /** Boxes overlapping this fraction of the smaller box are one instance (pipeline --unify); absent on older runs. */
  unify?: number;
  min_sharpness: number;
  min_det: number;
  raw_detections: number;
  kept_detections: number;
  tracks_default?: number;
  tracks_filtered?: number;
}

export interface CurationClip {
  slug: string;
  generated_at: string;
  source: ClipSource;
  model: { match_url: string; models: string; k: number; queries_per_track: number; confidence?: string };
  filters?: ClipFilters;
  shots: { id: number; start: number; end: number }[];
  tracks: CurationTrack[];
  video_url: string;
  /** Frames that have a colour mask overlay (`${overlay_base}f${frame}.png`), half resolution. */
  overlay_frames?: number[];
  overlay_base?: string;
  /** The same, including the garments filtered by default. */
  overlay_all_frames?: number[];
  overlay_all_base?: string;
}

export const FILTER_LABEL: Record<string, string> = {
  confidence: 'low confidence',
  mask_area: 'small mask',
  box_side: 'narrow or short box',
  box_area: 'small box',
  sharpness: 'out of focus',
};

/** One hue per track id (golden-angle spacing), for clips published before colours existed. */
export function trackColor(id: number): string {
  return `hsl(${(id * 137.508) % 360} 75% 50%)`;
}

export interface TrackSelection {
  chosen: number | null;
  alternates: number[];
  hidden: boolean;
  note: string;
  updated_at?: string;
  /** Publish chose the top candidate because nobody had decided ("when in doubt, decided"); stays until changed. */
  auto?: boolean;
  /** The choice came from the previous publish after a rerun (carried forward on Publish). */
  carried_from?: string | null;
  /** When a person confirmed the product Publish had chosen; set by the confirm call, cleared by nothing. */
  confirmed_at?: string;
}

/** A garment whose product Publish chose and no person has looked at yet (Jason's rule, 2026-10-22). */
export function isUnconfirmed(selection: TrackSelection | undefined): boolean {
  return Boolean(selection && selection.chosen !== null && selection.chosen !== undefined && !selection.hidden
    && (selection.auto || selection.carried_from));
}

export interface Selections {
  tracks: Record<string, TrackSelection>;
  updated_at: string | null;
  published_at: string | null;
}

export interface ClipSummary {
  /** Garments whose product Publish chose and nobody has confirmed. */
  n_unconfirmed?: number;
  slug: string;
  generated_at: string;
  source: ClipSource;
  n_tracks: number;
  n_decided: number;
  updated_at: string | null;
  published_at: string | null;
}

export interface PublishResult {
  slug: string;
  published_at: string;
  n_tracks: number;
  n_images: number;
}

/** One admin call: the site's API key plus X-Curation-Passcode, a JSON answer, and the API's own `detail`
 *  as the error message. Shared by the curation API and the board's viewers API. */
async function adminCall<T>(url: string, passcode: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'X-Curation-Passcode': passcode,
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
  };
  // The CRUD service wants the site's API key as well; the box ignores it.
  if (API_KEY) headers.Authorization = `Bearer ${API_KEY}`;
  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    let detail = `${response.status}`;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      /* not JSON */
    }
    if (response.status === 401) {
      // The CRUD service checks the site's API key before the passcode: without VITE_APP_API_KEY
      // (a local checkout with no .env) every passcode looked "wrong" (Felipe, 2026-10-22).
      throw new Error(
        /api key/i.test(detail)
          ? 'The site has no API key: set VITE_APP_API_KEY (Railway: Demo Site → ecomm → Variables) and restart'
          : 'Wrong passcode',
      );
    }
    throw new Error(detail);
  }
  return response.json();
}

export class CurationApi {
  constructor(private passcode: string) {}

  private call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return adminCall<T>(`${CURATION_URL}${path}`, this.passcode, init);
  }

  listClips() {
    return this.call<ClipSummary[]>('/clips');
  }

  getClip(slug: string) {
    return this.call<{ clip: CurationClip; selections: Selections }>(`/clips/${encodeURIComponent(slug)}`);
  }

  saveTrack(slug: string, trackId: number, selection: Omit<TrackSelection, 'updated_at'>) {
    return this.call<Selections>(`/clips/${encodeURIComponent(slug)}/tracks/${trackId}`, {
      method: 'PUT',
      body: JSON.stringify(selection),
    });
  }

  /** The admin says the product Publish chose for this garment is right: it stays, the auto mark goes. */
  confirmTrack(slug: string, trackId: number) {
    return this.call<Selections>(`/clips/${encodeURIComponent(slug)}/tracks/${trackId}/confirm`, { method: 'POST' });
  }

  publish(slug: string) {
    return this.call<PublishResult>(`/clips/${encodeURIComponent(slug)}/publish`, { method: 'POST' });
  }
}

// --- viewers (admin, SIL-1595) ---------------------------------------------------------------
// Who signed in to the sites that ask for a company email, and who may. The same gate as curation.

export type ViewerOutcome = 'ok' | 'refused' | 'blocked' | 'code_sent' | 'code_failed';

export interface ViewerSignIn {
  id: string;
  site: string;
  email: string;
  domain: string;
  /** ok: signed in. refused: the domain is not on the site's list. blocked: the address or domain is blocked.
   *  On a site that asks for a code (SIL-1618): code_sent, a code went out; code_failed, a wrong or expired code. */
  outcome: ViewerOutcome;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface ViewerRule {
  id: string;
  action: 'allow' | 'block';
  /** An address ("anna@wbd.com") or a domain ("wbd.com", which covers its subdomains). */
  pattern: string;
  /** The site it applies to, or null for every site. */
  site: string | null;
  note: string | null;
  created_at: string | null;
}

export interface ViewerSite {
  host: string;
  mode: SignInMode;
  /** How long one sign-in lasts there; null = the API's default (12 h). */
  cookie_ttl_seconds: number | null;
  label: string | null;
  /** The site also asks for a code emailed to the address (SIL-1618). */
  email_code?: boolean;
}

export interface NewViewerRule {
  action: 'allow' | 'block';
  pattern: string;
  site?: string | null;
  note?: string | null;
}

export class ViewersApi {
  constructor(private passcode: string) {}

  private call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return adminCall<T>(`${VIEWERS_URL}${path}`, this.passcode, init);
  }

  /** Recent sign-in attempts, newest first; one site's or everyone's. */
  listSignIns(site?: string, limit = 200) {
    const query = new URLSearchParams({ limit: String(limit) });
    if (site) query.set('site', site);
    return this.call<{ signins: ViewerSignIn[] }>(`/signins?${query}`);
  }

  listSites() {
    return this.call<{ sites: ViewerSite[] }>('/sites');
  }

  /** Every rule, or one site's plus the ones for every site. */
  listRules(site?: string) {
    return this.call<{ rules: ViewerRule[] }>(site ? `/rules?site=${encodeURIComponent(site)}` : '/rules');
  }

  addRule(rule: NewViewerRule) {
    return this.call<ViewerRule>('/rules', { method: 'POST', body: JSON.stringify(rule) });
  }

  removeRule(id: string) {
    return this.call<{ ok: boolean }>(`/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

// --- shared helpers ---------------------------------------------------------------------------

/** The hotspot position of a track at a frame, or null when the track is not on screen. */
export function trackPositionAt(
  track: { start_frame: number; end_frame: number; path: [number, number, number][] },
  frame: number,
): { x: number; y: number } | null {
  if (frame < track.start_frame || frame > track.end_frame || track.path.length === 0) return null;
  // Rows are [frame, x, y] in frame order. A bridged garment (the same top before and after a cut-away)
  // has no rows during the cut-away, so look the frame up instead of indexing from start_frame.
  let lo = 0;
  let hi = track.path.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const f = track.path[mid][0];
    if (f === frame) return { x: track.path[mid][1] * 100, y: track.path[mid][2] * 100 };
    if (f < frame) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
}

// Every listing link a shopper clicks goes through the affiliate redirect (jaysilvr, ecomm #98): it
// logs the click and, where Silvr has a programme (eBay's EPN), puts the affiliate parameters on the
// link so a purchase credits us. `source` tells the demo's clicks apart in the dashboard.
const REDIRECT_URL = (import.meta.env.VITE_APP_REDIRECT_URL || 'https://affiliate-redirect.silvr.fashion/out').replace(/\/$/, '');

/** The tracked form of a listing link. Applied where StoreItems are built (toStoreItem), never in a
 *  product file, so no hand-entered link bypasses it. A link already through the redirect is left alone. */
export function trackedUrl(url: string, source: string): string {
  if (!url || url.startsWith(REDIRECT_URL)) return url;
  return `${REDIRECT_URL}?u=${encodeURIComponent(url)}&source=${encodeURIComponent(source)}`;
}

/** Where to send a shopper for a catalog product: the listing has no URL, so a search on the
 *  merchant's site is the closest thing to "Shop at store" until the catalog lookup lands. */
export function storeSearchUrl(product: { brand: string | null; name: string | null; merchant: string | null }): string {
  const query = [product.brand, product.name].filter(Boolean).join(' ') || product.merchant || 'silvr';
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

/** The last segment of a Google product taxonomy path, e.g. "Dresses". */
export function shortCategory(category: string | null | undefined): string {
  if (!category) return '';
  const parts = category.split('>').map((p) => p.trim());
  return parts[parts.length - 1];
}
